import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import {
  ArrowRight, Sparkles, TrendingUp, MapPin, Search, Building2,
  CheckCircle2, FileText, Mail, Award, BarChart3, Target,
} from 'lucide-react';
import { API } from '../utils/api';
import {
  MARKET_OVERVIEW, TOP_KEYWORDS, KEYWORD_THEMES, DEMAND_BY_CITY,
  LONGTAIL_QUESTIONS, MARKETING_TIPS, B2B_FAQ,
} from '../data/keywordsCentrosRehabilitacion';
import KeywordTable from '../components/KeywordTable';
import MarketingTipCard from '../components/MarketingTipCard';
import { getAuthorBySlug, buildAuthorPersonSchema } from '../config/authors';

const SITE_URL = 'https://sinadicciones.org';
const PAGE_URL = `${SITE_URL}/marketing-centros-rehabilitacion`;
const TITLE = 'Marketing Digital para Centros de Rehabilitación en Chile | Estrategia + Datos Reales';
const DESC =
  '27.700 búsquedas/mes en Chile buscan un centro de rehabilitación. Datos de mercado, palabras clave, tips de marketing accionables y el directorio Nº1 de Chile para captar pacientes calificados.';

// --------------------- SEO HOOK ---------------------
function useSeo(stats, articles) {
  useEffect(() => {
    const nelson = getAuthorBySlug('nelson-gonzalez');
    const ogImage = `${API}/public/marketing/og-image`;

    document.title = TITLE;

    const setMeta = (name, content, attr = 'name') => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', DESC);
    setMeta('author', nelson?.name || 'Nelson González');
    setMeta('og:title', TITLE, 'property');
    setMeta('og:description', DESC, 'property');
    setMeta('og:type', 'article', 'property');
    setMeta('og:url', PAGE_URL, 'property');
    setMeta('og:image', ogImage, 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', TITLE);
    setMeta('twitter:description', DESC);
    setMeta('twitter:image', ogImage);
    setMeta('article:author', `${SITE_URL}/autor/nelson-gonzalez`, 'property');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', PAGE_URL);

    const blocks = [
      {
        id: 'mkt-article-jsonld',
        body: {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: TITLE,
          description: DESC,
          image: ogImage,
          author: nelson ? buildAuthorPersonSchema(nelson) : undefined,
          publisher: {
            '@type': 'Organization',
            name: 'SinAdicciones.org',
            logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` },
          },
          datePublished: '2026-06-07',
          dateModified: new Date().toISOString().slice(0, 10),
          mainEntityOfPage: PAGE_URL,
          inLanguage: 'es-CL',
          articleSection: 'Marketing',
          keywords: 'marketing centros rehabilitación, captación pacientes, directorio centros chile, SEO clínicas',
        },
      },
      {
        id: 'mkt-dataset-jsonld',
        body: {
          '@context': 'https://schema.org',
          '@type': 'Dataset',
          name: 'Demanda de búsqueda: Centros de rehabilitación en Chile (2026)',
          description: `${MARKET_OVERVIEW.totalKeywords} palabras clave, ${MARKET_OVERVIEW.totalVolume} búsquedas mensuales totales relacionadas con "centro de rehabilitación" en Chile.`,
          creator: { '@type': 'Organization', name: 'SinAdicciones.org', url: SITE_URL },
          keywords: TOP_KEYWORDS.map((k) => k.kw),
          datePublished: MARKET_OVERVIEW.lastUpdated,
          inLanguage: 'es-CL',
          isAccessibleForFree: true,
        },
      },
      {
        id: 'mkt-breadcrumb-jsonld',
        body: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Inicio', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: 'Marketing para Centros', item: PAGE_URL },
          ],
        },
      },
      {
        id: 'mkt-faq-jsonld',
        body: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: B2B_FAQ.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
      },
    ];

    blocks.forEach((b) => {
      const old = document.getElementById(b.id);
      if (old) old.remove();
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.id = b.id;
      s.text = JSON.stringify(b.body);
      document.head.appendChild(s);
    });

    return () => {
      blocks.forEach((b) => {
        const old = document.getElementById(b.id);
        if (old) old.remove();
      });
    };
  }, [stats, articles]);
}

// --------------------- COMPONENTS ---------------------
function BigNumber({ value, label, sub, tone = 'emerald', testId }) {
  const toneMap = {
    emerald: 'text-emerald-400',
    amber: 'text-amber-400',
    cyan: 'text-cyan-400',
    violet: 'text-violet-400',
  };
  return (
    <div
      className="p-6 bg-slate-900/60 border border-slate-800 rounded-2xl"
      data-testid={testId}
    >
      <p className={`text-4xl md:text-5xl font-bold ${toneMap[tone]} leading-none`}>{value}</p>
      <p className="text-white font-medium mt-3 text-sm">{label}</p>
      {sub && <p className="text-slate-400 text-xs mt-1">{sub}</p>}
    </div>
  );
}

function DemandBar({ city, vol, max, opportunity }) {
  const w = Math.max(8, (vol / max) * 100);
  const opColor = {
    'Alta': 'text-emerald-300 bg-emerald-500/10',
    'Media-Alta': 'text-cyan-300 bg-cyan-500/10',
    'Media': 'text-amber-300 bg-amber-500/10',
  }[opportunity] || 'text-slate-300 bg-slate-700/30';
  return (
    <div className="flex items-center gap-3" data-testid={`demand-${city}`}>
      <div className="w-28 sm:w-36 text-sm text-slate-300 text-right truncate">{city}</div>
      <div className="flex-1 bg-slate-900 rounded-full h-7 overflow-hidden border border-slate-800">
        <div
          className="h-full bg-gradient-to-r from-emerald-700 to-emerald-400 flex items-center justify-end pr-2"
          style={{ width: `${w}%` }}
        >
          <span className="text-[10px] text-white font-bold">{vol.toLocaleString('es-CL')}</span>
        </div>
      </div>
      <span className={`text-xs font-medium px-2 py-0.5 rounded hidden sm:inline-block ${opColor}`}>
        {opportunity}
      </span>
    </div>
  );
}

// --------------------- MAIN ---------------------
export default function MarketingCentrosPage() {
  const [stats, setStats] = useState(null);
  const [marketingArticles, setMarketingArticles] = useState([]);

  useEffect(() => {
    axios.get(API + '/public/stats').then((r) => setStats(r.data)).catch(() => setStats(null));
    axios.get(API + '/articles/public', { params: { limit: 100 } })
      .then((r) => {
        const arts = r.data?.articles || r.data || [];
        const filtered = arts.filter(
          (a) => (a.category || '').toLowerCase() === 'marketing',
        );
        setMarketingArticles(filtered);
      })
      .catch(() => setMarketingArticles([]));
  }, []);

  useSeo(stats, marketingArticles);

  const maxCityVol = useMemo(
    () => Math.max(...DEMAND_BY_CITY.map((d) => d.monthlyVol), 1),
    [],
  );
  const totalLeadsMonth = 593; // del estudio Reto 21 — número estable

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main>
        {/* ============== HERO ============== */}
        <section className="relative overflow-hidden border-b border-slate-800">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-950/40 via-slate-950 to-amber-950/20" />
          <div
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(circle at 25% 30%, rgba(16,185,129,.18), transparent 50%), radial-gradient(circle at 75% 70%, rgba(245,158,11,.12), transparent 50%)',
            }}
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 md:pt-36 md:pb-24">
            <div className="max-w-3xl">
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-medium mb-4"
                data-testid="hero-badge"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Para directores y marketers de centros de rehabilitación
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight">
                <span className="text-emerald-400">27.700 búsquedas/mes</span> en Chile están buscando un centro de rehabilitación.
                <br />
                <span className="text-slate-300">¿Encuentran el tuyo?</span>
              </h1>
              <p className="text-base md:text-lg text-slate-300 mt-6 max-w-2xl">
                Datos reales del mercado chileno, palabras clave, demanda por ciudad
                y la estrategia para que tu centro capte pacientes calificados desde Google.
              </p>

              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/agregar-centro">
                  <Button
                    size="lg"
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    data-testid="hero-cta-register"
                  >
                    Registrar mi centro gratis
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/planes">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-slate-700 text-slate-200 hover:bg-slate-800"
                    data-testid="hero-cta-plans"
                  >
                    Ver planes y precios
                  </Button>
                </Link>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-10 max-w-2xl">
                <div className="text-center sm:text-left">
                  <p className="text-2xl font-bold text-emerald-400">{stats?.centers || '207'}+</p>
                  <p className="text-xs text-slate-400">Centros en el directorio</p>
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-2xl font-bold text-amber-400">{totalLeadsMonth}</p>
                  <p className="text-xs text-slate-400">Leads/mes calificados</p>
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-2xl font-bold text-cyan-400">{stats?.cities || '50'}+</p>
                  <p className="text-xs text-slate-400">Ciudades cubiertas</p>
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-2xl font-bold text-violet-400">{stats?.regions || '16'}</p>
                  <p className="text-xs text-slate-400">Regiones de Chile</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============== EL MERCADO EN NÚMEROS ============== */}
        <section className="border-b border-slate-800 bg-slate-900/20" data-testid="market-overview">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="max-w-2xl mb-10">
              <p className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-2 flex items-center gap-2">
                <BarChart3 className="w-3.5 h-3.5" /> Mercado en Chile · 2026
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Las cifras que tu competencia <span className="text-emerald-400">no está viendo</span>
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <BigNumber
                testId="big-volume"
                value={`${(MARKET_OVERVIEW.totalVolume / 1000).toFixed(1)}K`}
                label="Búsquedas mensuales"
                sub="Total Chile, todas las variantes"
                tone="emerald"
              />
              <BigNumber
                testId="big-keywords"
                value={MARKET_OVERVIEW.totalKeywords.toLocaleString('es-CL')}
                label="Palabras clave activas"
                sub="Long-tail incluido"
                tone="cyan"
              />
              <BigNumber
                testId="big-kd"
                value={`${MARKET_OVERVIEW.avgKD}%`}
                label="Dificultad media"
                sub="Bajo = fácil rankear orgánico"
                tone="amber"
              />
              <BigNumber
                testId="big-cpc"
                value={`$${MARKET_OVERVIEW.avgCPC.toFixed(2)}`}
                label="CPC promedio (USD)"
                sub="Lo que pagarías por click en Ads"
                tone="violet"
              />
            </div>
            <p className="text-sm text-slate-400 mt-6 italic">
              Fuente: Keyword Surfer (junio 2026). Datos agregados de Google Chile.
            </p>
          </div>
        </section>

        {/* ============== TOP KEYWORDS ============== */}
        <section className="border-b border-slate-800" data-testid="top-keywords">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid md:grid-cols-12 gap-10 items-start mb-8">
              <div className="md:col-span-5">
                <p className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-2 flex items-center gap-2">
                  <Search className="w-3.5 h-3.5" /> Qué buscan los pacientes
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                  Las 15 palabras clave que dominan la conversión
                </h2>
                <p className="text-slate-300 mt-4">
                  El <strong className="text-white">38%</strong> de las búsquedas son comerciales.
                  Más de <strong className="text-emerald-400">1.500 búsquedas/mes</strong> incluyen
                  &ldquo;<em>gratis</em>&rdquo; — ahí hay oportunidad para captar leads con un gancho introductorio.
                </p>
                <div className="mt-6 space-y-2 text-sm text-slate-400">
                  <p className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded bg-emerald-500/40 border border-emerald-500/40" />
                    KD ≤ 19 — Fácil de rankear
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded bg-amber-500/40 border border-amber-500/40" />
                    KD 20-39 — Esfuerzo medio
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded bg-red-500/40 border border-red-500/40" />
                    KD ≥ 40 — Solo con backlinks
                  </p>
                </div>
              </div>
              <div className="md:col-span-7">
                <KeywordTable keywords={TOP_KEYWORDS} maxRows={15} />
              </div>
            </div>
          </div>
        </section>

        {/* ============== DEMANDA POR CIUDAD ============== */}
        <section className="border-b border-slate-800 bg-slate-900/20" data-testid="demand-cities">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="max-w-2xl mb-10">
              <p className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-2 flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" /> Demanda territorial
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                Cómo se distribuye la demanda en Chile
              </h2>
              <p className="text-slate-300 mt-3">
                Santiago concentra el grueso, pero hay <strong className="text-white">8 ciudades secundarias</strong> con
                oportunidad clara: menor competencia, demanda real, CPCs más bajos.
              </p>
            </div>
            <div className="space-y-2.5 max-w-3xl">
              {DEMAND_BY_CITY.map((d) => (
                <DemandBar
                  key={d.city}
                  city={d.city}
                  vol={d.monthlyVol}
                  max={maxCityVol}
                  opportunity={d.opportunity}
                />
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-6 italic">
              * Volumen mensual sumado de keywords con esa ciudad en la búsqueda. Oportunidad calculada como ratio demanda/competencia local.
            </p>
          </div>
        </section>

        {/* ============== PREGUNTAS LONG-TAIL ============== */}
        <section className="border-b border-slate-800" data-testid="longtail-questions">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="max-w-2xl mb-10">
              <p className="text-xs uppercase tracking-widest text-amber-400 font-semibold mb-2 flex items-center gap-2">
                <Target className="w-3.5 h-3.5" /> Long-tail gratis y sin competencia
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                7 preguntas reales que <span className="text-amber-400">nadie está respondiendo</span>
              </h2>
              <p className="text-slate-300 mt-3">
                Estas búsquedas tienen <strong className="text-white">cero competencia directa</strong>. Crear FAQs o artículos
                cortos en tu blog respondiéndolas con Schema.org te puede posicionar en 4-6 semanas.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {LONGTAIL_QUESTIONS.map((q, i) => (
                <div
                  key={i}
                  className="p-4 bg-slate-900/40 border border-slate-800 rounded-xl hover:border-amber-500/30 transition-colors"
                  data-testid={`longtail-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-amber-400 font-bold text-sm">?</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium leading-snug">{q.q}</p>
                      <p className="text-xs text-slate-500 mt-1 capitalize">Intent: {q.topic}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============== POR QUÉ UN DIRECTORIO ESPECIALIZADO ============== */}
        <section className="border-b border-slate-800 bg-slate-900/20" data-testid="why-directory">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="max-w-2xl mb-10">
              <p className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-2 flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5" /> Estar en un directorio importa
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                Por qué tu centro <span className="text-emerald-400">necesita</span> un directorio especializado
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: 'SEO compartido',
                  desc: 'Cada uno de los 207 centros suma backlinks, contenido y autoridad al dominio. Tu perfil hereda parte de ese link equity y rankea más rápido que un sitio nuevo individual.',
                  icon: TrendingUp,
                },
                {
                  title: 'Trust score B2C',
                  desc: 'Los pacientes confían más en directorios curados con verificación, reseñas reales y filtros por modalidad/región que en webs corporativas individuales. Un perfil en SinAdicciones.org transmite legitimidad.',
                  icon: CheckCircle2,
                },
                {
                  title: 'Lead intent calificado',
                  desc: 'Quien llega a sinadicciones.org ya tomó la decisión de buscar ayuda. No es tráfico exploratorio: es lead caliente. Nuestro matching automático te conecta solo con quienes calzan con tu modalidad y región.',
                  icon: Award,
                },
              ].map((b) => (
                <Card
                  key={b.title}
                  className="bg-slate-900/60 border-slate-800 hover:border-emerald-500/30 transition-colors"
                  data-testid={`benefit-${b.title.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <CardContent className="p-6">
                    <div className="w-11 h-11 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-4">
                      <b.icon className="w-5 h-5 text-emerald-400" />
                    </div>
                    <h3 className="text-white font-bold text-lg">{b.title}</h3>
                    <p className="text-slate-300 text-sm mt-2 leading-relaxed">{b.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ============== TIPS ACCIONABLES ============== */}
        <section className="border-b border-slate-800" data-testid="marketing-tips">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="max-w-2xl mb-10">
              <p className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-2">
                Guía accionable
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                7 tips de marketing que sí mueven la aguja
              </h2>
              <p className="text-slate-300 mt-3">
                Aplicables esta semana. Sin agencias, sin presupuesto de $1M+.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {MARKETING_TIPS.map((t) => (
                <MarketingTipCard key={t.number} tip={t} />
              ))}
            </div>
          </div>
        </section>

        {/* ============== ARTÍCULOS CATEGORÍA MARKETING ============== */}
        <section className="border-b border-slate-800 bg-slate-900/20" data-testid="marketing-articles">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
              <div>
                <p className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-2">
                  Biblioteca de marketing
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                  Guías publicadas
                </h2>
                <p className="text-slate-300 mt-2">
                  Escritos por <Link to="/autor/nelson-gonzalez" className="text-emerald-400 hover:underline">Nelson González</Link>,
                  fundador de SinAdicciones.org.
                </p>
              </div>
              <Link to="/blog" className="text-sm text-emerald-400 hover:underline">
                Ver todo el blog →
              </Link>
            </div>
            {marketingArticles.length === 0 ? (
              <Card className="bg-slate-900/40 border-slate-800">
                <CardContent className="p-10 text-center">
                  <FileText className="w-10 h-10 text-slate-700 mx-auto mb-3" />
                  <p className="text-slate-400">Cargando guías de marketing...</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="marketing-articles-grid">
                {marketingArticles.map((a) => (
                  <Link
                    key={a.article_id}
                    to={`/blog/${a.slug}`}
                    data-testid={`marketing-article-${a.slug}`}
                  >
                    <Card className="bg-slate-900/60 border-slate-800 hover:border-emerald-500/40 transition-colors h-full group">
                      {a.image && (
                        <div className="h-40 overflow-hidden rounded-t-lg">
                          <img
                            src={a.image}
                            alt={a.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      )}
                      <CardContent className="p-5">
                        <span className="inline-block text-[10px] uppercase tracking-wider font-bold text-emerald-400 mb-2">
                          {a.category}
                        </span>
                        <h3 className="text-white font-semibold leading-snug group-hover:text-emerald-400 transition-colors line-clamp-2">
                          {a.title}
                        </h3>
                        {a.excerpt && (
                          <p className="text-slate-400 text-sm mt-2 line-clamp-3">{a.excerpt}</p>
                        )}
                        <p className="text-emerald-400 text-sm font-medium mt-3 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                          Leer guía <ArrowRight className="w-4 h-4" />
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ============== PLANES SUMMARY + CTA ============== */}
        <section
          className="border-b border-slate-800 bg-gradient-to-br from-emerald-950/30 to-slate-950"
          data-testid="plans-summary"
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Empieza gratis · Crece con planes desde <span className="text-emerald-400">$99.000 CLP</span>
              </h2>
              <p className="text-slate-300 mt-3">
                4 niveles según tu objetivo: visibilidad, lead generation o dominio total de tu región.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  name: 'Gratuito',
                  price: '$0',
                  period: 'siempre',
                  color: 'border-slate-700',
                  bullets: ['Perfil en directorio', 'Información de contacto', 'Recibir reseñas'],
                  cta: 'Registrar gratis',
                  href: '/agregar-centro',
                },
                {
                  name: 'Destacado',
                  price: '$99.000',
                  period: '/mes',
                  color: 'border-slate-700',
                  bullets: ['Posición Top 20', 'Badge "Destacado"', 'Estadísticas básicas'],
                  cta: 'Cotizar',
                  href: '/planes',
                },
                {
                  name: 'Premium',
                  price: '$199.000',
                  period: '/mes',
                  color: 'border-amber-500/40',
                  bullets: ['Posición Top 4-10', 'Badge "Premium" + fotos', 'Prioridad en TerapeutAI'],
                  cta: 'Cotizar',
                  href: '/planes',
                  featured: true,
                },
                {
                  name: 'Elite',
                  price: '$350.000',
                  period: '/mes',
                  color: 'border-emerald-500/40',
                  bullets: ['Posición Top 1-3', 'Máx 2 por modalidad/región', 'Destacado en landing + soporte VIP'],
                  cta: 'Solicitar cupo',
                  href: '/planes',
                },
              ].map((p) => (
                <Card
                  key={p.name}
                  className={`bg-slate-900/60 border ${p.color} ${p.featured ? 'shadow-lg shadow-amber-500/10' : ''}`}
                  data-testid={`plan-${p.name.toLowerCase()}`}
                >
                  <CardContent className="p-5">
                    <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                      Plan
                    </p>
                    <h3 className="text-white text-xl font-bold mt-1">{p.name}</h3>
                    <p className="text-emerald-400 text-2xl font-bold mt-2">
                      {p.price}
                      <span className="text-xs text-slate-500 font-normal"> {p.period === 'siempre' ? 'siempre' : `${p.period} CLP`}</span>
                    </p>
                    <ul className="space-y-1.5 mt-4 text-sm text-slate-300">
                      {p.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to={p.href} className="block mt-5">
                      <Button
                        size="sm"
                        className={`w-full ${
                          p.featured
                            ? 'bg-amber-500 hover:bg-amber-600'
                            : 'bg-emerald-500 hover:bg-emerald-600'
                        }`}
                        data-testid={`plan-cta-${p.name.toLowerCase()}`}
                      >
                        {p.cta}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/planes" className="text-sm text-slate-400 hover:text-emerald-400 underline">
                Comparativa completa de planes →
              </Link>
            </div>
          </div>
        </section>

        {/* ============== FAQ ============== */}
        <section className="border-b border-slate-800" data-testid="b2b-faq">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Preguntas de centros</h2>
            <p className="text-slate-400 mt-3 mb-8">
              Las dudas más frecuentes antes de unirte al directorio.
            </p>
            <Accordion type="single" collapsible className="space-y-2">
              {B2B_FAQ.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="border border-slate-800 rounded-lg px-4 bg-slate-900/40"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="text-left text-white hover:text-emerald-300 hover:no-underline py-4">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-300 pb-4 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* ============== FINAL CTA ============== */}
        <section
          className="bg-gradient-to-br from-emerald-950/40 via-slate-900 to-slate-950"
          data-testid="final-cta"
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight">
              Tu próximo paciente te está buscando <span className="text-emerald-400">ahora</span>
            </h2>
            <p className="text-slate-300 mt-4 max-w-2xl mx-auto text-base md:text-lg">
              Más de 593 personas piden ayuda cada mes en SinAdicciones.org.
              Registra tu centro en 5 minutos y empieza a recibir leads esta semana.
            </p>
            <div className="flex flex-wrap gap-3 justify-center mt-8">
              <Link to="/agregar-centro">
                <Button
                  size="lg"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white text-base"
                  data-testid="final-cta-register"
                >
                  Registrar mi centro gratis
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/planes">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-slate-700 text-slate-200 hover:bg-slate-800 text-base"
                  data-testid="final-cta-cotizar"
                >
                  Cotizar plan Premium
                </Button>
              </Link>
              <a href="mailto:contacto@sinadicciones.cl?subject=Quiero hablar con un asesor">
                <Button
                  size="lg"
                  variant="ghost"
                  className="text-slate-300 hover:text-emerald-400 hover:bg-transparent text-base"
                  data-testid="final-cta-asesor"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Hablar con un asesor
                </Button>
              </a>
            </div>
            <p className="text-xs text-slate-500 mt-8">
              Sin permanencia. Cancela cuando quieras desde tu panel.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
